import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '@/hooks/useAuth';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { loginRequest } from '@/lib/msalConfig';
import { createLoginNonce, exchangeMicrosoftIdToken } from '@/lib/microsoftTokenExchange';
import { useApplyTheme } from '@/hooks/useApplyTheme';
import { useAccessCheck } from '@/hooks/useAdmin';
import { resolveAccessGate } from '@/lib/accessGate';
import {
  clearBridgeRedirect,
  markBridgeRedirect,
  readBridgeRedirectAt,
  resolveBridgeNotice,
  shouldAttemptBridgeRedirect,
  type BridgeFailureReason,
} from '@/lib/msBridge';
import { AppShell } from './AppShell';
import { GateCard, GateSpinner } from './GateCard';
import { Button } from '@/components/ui/Button';
import { Shield, LogOut, RefreshCw } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { isDevShellEnabled } from '@/dev/devShell';

/** Returns true once `active` has stayed true continuously for `ms` milliseconds. */
function useStuckTimer(active: boolean, ms: number): boolean {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    if (!active) {
      setStuck(false);
      return;
    }
    const timer = setTimeout(() => setStuck(true), ms);
    return () => clearTimeout(timer);
  }, [active, ms]);
  return stuck;
}

/**
 * The sign-in gate. Decides WHETHER the app may be shown and, when it may not,
 * which recovery screen explains why; the frame itself is AppShell.
 */
export function AppLayout() {
  useApplyTheme();
  const { user, loading } = useAuth();
  const { isAuthenticated: isMsAuthenticated, isLoading: isMsLoading, user: msUser } = useMicrosoftAuth();
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();
  const { signOut } = useAuthStore();
  const bridgeInFlight = useRef(false);
  // The account we've already reached a definitive answer for, so a re-render
  // can't re-run a bridge that has nothing new to try.
  const bridgeSettledFor = useRef<string | null>(null);
  const [bridgeFailure, setBridgeFailure] = useState<BridgeFailureReason | null>(null);

  // Bridge Microsoft-only sessions to a real Supabase session. Without one,
  // every Supabase query runs on the anon key. Most attempts resolve silently
  // from the cached ID token; when that token can't be paired with a nonce we
  // know, one invisible (prompt: 'none') round-trip to Entra mints one that
  // can. The redirect is rate-limited rather than one-shot — the old
  // sessionStorage flag survived reloads, so a single failure wedged the tab
  // for good (see msBridge).
  useEffect(() => {
    if (loading || isMsLoading || user || !isMsAuthenticated || msalAccounts.length === 0) return;
    if (bridgeInFlight.current) return;

    const accountId = msalAccounts[0].homeAccountId;
    if (bridgeSettledFor.current === accountId) return;
    bridgeInFlight.current = true;

    (async () => {
      let failure: BridgeFailureReason;
      try {
        const result = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account: msalAccounts[0],
        });
        const exchange = await exchangeMicrosoftIdToken(result.idToken);
        if (exchange.ok) {
          setBridgeFailure(null);
          return;
        }
        failure = exchange.reason;
      } catch (err) {
        console.warn('[AppLayout] Silent Supabase bridge failed:', err);
        failure = 'entra-silent-failed';
      }

      // Settle: report it and stop. A refused signup means the identity is
      // valid but no Flow account exists — no number of trips through Entra
      // conjures one, and pretending otherwise is what produced an endless
      // spinner instead of a sentence an admin could act on. A refused ban is
      // the same: the identity is fine and the access was taken away on purpose.
      const settle = () => {
        bridgeSettledFor.current = accountId;
        setBridgeFailure(failure);
      };

      if (failure === 'signup-disabled' || failure === 'user-banned') return settle();
      if (!shouldAttemptBridgeRedirect(readBridgeRedirectAt(), Date.now())) return settle();

      markBridgeRedirect(Date.now());
      try {
        const hashedNonce = await createLoginNonce();
        await msalInstance.loginRedirect({
          ...loginRequest,
          nonce: hashedNonce,
          prompt: 'none',
          redirectStartPage: window.location.href,
        });
      } catch (err) {
        console.warn('[AppLayout] Supabase bridge redirect failed:', err);
        settle();
      }
    })().finally(() => {
      bridgeInFlight.current = false;
    });
  }, [loading, isMsLoading, user, isMsAuthenticated, msalAccounts, msalInstance]);

  // Get the user's email from either auth system
  const currentEmail = user?.email || msUser?.email || '';

  // Check if user is in the allowed list. 'allowed' | 'denied' | 'unverifiable'
  // ('unverifiable' = the session token couldn't be verified — recoverable, NOT
  // a real denial; see migration 119 + admin.service.classifyFlowAccess).
  // Gate the check on a real Supabase session (see useAccessCheck): a
  // Microsoft-SSO user only has a JWT email once the token-exchange bridge
  // above has run, so passing !!user keeps the RPC from firing on the anon key.
  const {
    data: access,
    isLoading: isCheckingAccess,
    isError: isAccessError,
    refetch: refetchAccess,
  } = useAccessCheck(currentEmail, !!user);
  // The RPC now also answers "is this session an admin" (migration 181); the
  // entry gate only cares about allow/deny. usePermissions reads the admin half
  // from this same cached query.
  const accessStatus = access?.status;

  const gate = resolveAccessGate({
    authLoading: loading,
    msLoading: isMsLoading,
    hasSupabaseSession: !!user,
    isMsAuthenticated,
    isCheckingAccess,
    isAccessError,
    accessStatus,
  });

  // If verification stalls — whether waiting for the Supabase session or the
  // access check itself — surface a manual escape hatch instead of an endless
  // spinner. The threshold tracks the real budget: the bridge resolves in a
  // second or two, while a healthy access check can legitimately spend 8s per
  // attempt across three attempts (useAccessCheck). Crying "taking longer than
  // usual" at 6s taught everyone to ignore the one screen that was telling the
  // truth.
  const verificationSlow = useStuckTimer(
    gate === 'awaiting-session' || gate === 'verifying',
    gate === 'awaiting-session' ? 8000 : 15000
  );

  // The auth providers themselves can hang — MSAL wedged mid-interaction leaves
  // isMsLoading true forever. That spinner had no escape hatch at all.
  const initSlow = useStuckTimer(gate === 'loading', 12000);

  // Reload is only a real retry if it also clears the bridge's redirect
  // cooldown; otherwise the reloaded tab repeats the attempt that just failed.
  const reloadAndRetry = () => {
    clearBridgeRedirect();
    window.location.reload();
  };

  // DEV-ONLY shell preview (src/dev/devShell.ts): draw the frame without a
  // session so it can be looked at. Folded out of production builds, and it
  // unlocks no data — with no session every query runs on the anon key.
  const devShell = import.meta.env.DEV && isDevShellEnabled();

  // Show loading state while the auth providers initialize — with the same
  // escape hatch as the later gates once it stops looking like initialization.
  if (!devShell && gate === 'loading') {
    if (!initSlow) {
      return <GateSpinner label="Loading…" />;
    }
    return (
      <GateCard
        title="Still starting up…"
        body="Sign-in is taking longer than it should. Reloading usually clears it."
        actions={
          <>
            <Button variant="primary" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={reloadAndRetry}>
              Reload
            </Button>
            <Button variant="ghost" leftIcon={<LogOut className="h-4 w-4" />} onClick={() => signOut()}>
              Sign in again
            </Button>
          </>
        }
      />
    );
  }

  // Redirect to login if not authenticated with either Supabase or Microsoft
  if (!devShell && gate === 'redirect-login') {
    return <Navigate to="/login" replace />;
  }

  // Either waiting for the Microsoft→Supabase session bridge, or the access
  // check is in flight. Both show a spinner, with an escape hatch if it stalls
  // or errors. Holding here (instead of running the check without a session) is
  // what prevents the transient "Couldn't verify your access" screen.
  if (!devShell && (gate === 'awaiting-session' || gate === 'verifying')) {
    // A definitive verdict from the bridge beats the timer — there is nothing
    // left to wait for, so don't spend 8 seconds implying there is.
    const showStuck = isAccessError || verificationSlow || bridgeFailure !== null;
    if (!showStuck) {
      return <GateSpinner label="Verifying access…" />;
    }

    // What actually went wrong decides both the wording and which recovery
    // leads. Telling an unprovisioned user to check their connection is how
    // this stayed invisible for weeks.
    const notice = resolveBridgeNotice(bridgeFailure);
    const reloadButton = (
      <Button
        variant={notice.primary === 'reload' ? 'primary' : 'secondary'}
        leftIcon={<RefreshCw className="h-4 w-4" />}
        onClick={reloadAndRetry}
      >
        Reload
      </Button>
    );
    const signInButton = (
      <Button
        variant={notice.primary === 'signin' ? 'primary' : 'ghost'}
        leftIcon={<LogOut className="h-4 w-4" />}
        onClick={() => signOut()}
      >
        Sign in again
      </Button>
    );

    return (
      <GateCard
        title={notice.title}
        body={notice.body}
        detail={notice.detail}
        actions={
          <>
            {notice.primary === 'signin' ? (
              <>
                {signInButton}
                {reloadButton}
              </>
            ) : (
              <>
                {reloadButton}
                {signInButton}
              </>
            )}
            {/* Only offer the refetch-based retry once a Supabase session
                exists. In React Query v5 refetch() ignores `enabled: false`,
                so retrying during 'awaiting-session' would run the check on
                the anon key and cache a spurious 'unverifiable' — reviving
                the very screen this guards against. Before the session lands,
                Reload (re-runs the bridge) and Sign in again are the recovery
                paths. */}
            {!!user && (
              <Button variant="secondary" onClick={() => refetchAccess()}>
                Try again
              </Button>
            )}
          </>
        }
      />
    );
  }

  // Couldn't verify the session token (no email claim reached the server) — a
  // token-exchange hiccup, NOT a real denial. Offer recovery (re-sign-in / retry)
  // instead of hard-locking a legitimate, allow-listed user. With the session
  // gate above, this is now reached only for a genuine, persistent case: a real
  // Supabase session whose token still carries no email claim.
  if (!devShell && gate === 'unverifiable') {
    return (
      <GateCard
        title="Couldn't verify your access"
        body="Your sign-in session couldn't be confirmed. This is usually a temporary hiccup — signing in again clears it."
        actions={
          <>
            <Button variant="primary" leftIcon={<LogOut className="h-4 w-4" />} onClick={() => signOut()}>
              Sign in again
            </Button>
            <Button variant="secondary" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={() => refetchAccess()}>
              Try again
            </Button>
          </>
        }
      >
        <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
          If it keeps happening, contact your administrator.
        </p>
      </GateCard>
    );
  }

  // Positive denial only: the token carried an email that is genuinely not on
  // the allowlist. (A missing/unverifiable token is handled above, never here.)
  if (!devShell && gate === 'denied') {
    return (
      <GateCard
        icon={Shield}
        tone="danger"
        title="Access Restricted"
        body={
          <>
            Your account{' '}
            <strong className="font-semibold text-gray-700 dark:text-gray-300">{currentEmail}</strong>{' '}
            does not have access to Flow.
          </>
        }
        actions={
          <Button variant="secondary" leftIcon={<LogOut className="h-4 w-4" />} onClick={() => signOut()}>
            Sign Out
          </Button>
        }
      >
        <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
          Please contact your administrator to request access.
        </p>
        <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
          Working with our research team?{' '}
          <a href="/portal" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
            Sign in at the research portal
          </a>
          .
        </p>
      </GateCard>
    );
  }

  return <AppShell />;
}
